import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import './AccountManagement.css';

const AccountManagement = () => {
  const [accountType, setAccountType] = useState('users');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [search, setSearch] = useState(''); // client-side filter (name/phone/email)
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [accountDetails, setAccountDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Location update state
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationData, setLocationData] = useState({ lat: '', lng: '' });
  const [locationLoading, setLocationLoading] = useState(false);

  // Fetch accounts based on type
  useEffect(() => {
    fetchAccounts();
  }, [accountType]);

  const fetchAccounts = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      let response;
      switch (accountType) {
        case 'users':
          response = await adminAPI.getAllUsers();
          break;
        case 'restaurants':
          response = await adminAPI.getAllRestaurants();
          break;
        case 'delivery':
          response = await adminAPI.getAllDeliveryPartners();
          break;
        default:
          return;
      }

      const data = response.data.data;
      const accountsList = data.users || data.restaurants || data.deliveryPartners || [];

      // Debug logging
      console.log('[AccountManagement] Fetched accounts:', accountsList.length);
      if (accountType === 'restaurants' && accountsList.length > 0) {
        console.log('[AccountManagement] First restaurant data:', JSON.stringify(accountsList[0], null, 2));
        console.log('[AccountManagement] First restaurant has subscriptionPlan:', !!accountsList[0].subscriptionPlan);
      }

      setAccounts(accountsList);

      if (accountsList.length === 0) {
        setMessage({
          type: 'warning',
          text: `No ${accountType} found.`
        });
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      setMessage({
        type: 'error',
        text: `Failed to fetch ${accountType}. Please try again.`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBlockAction = (account) => {
    setSelectedAccount(account);
    setBlockReason('');
    setShowBlockModal(true);
  };

  const handleUnblockAction = async (account) => {
    const confirmed = window.confirm(
      `Are you sure you want to unblock ${account.name || account.email}?`
    );

    if (!confirmed) return;

    await updateBlockStatus(account._id, false, '');
  };

  const handleBlockSubmit = async () => {
    if (!blockReason.trim()) {
      alert('Please provide a reason for blocking this account.');
      return;
    }

    await updateBlockStatus(selectedAccount._id, true, blockReason);
    setShowBlockModal(false);
    setSelectedAccount(null);
    setBlockReason('');
  };

  // Force-logout: revoke all active sessions WITHOUT blocking the account.
  const handleRevokeSessions = async (account) => {
    const confirmed = window.confirm(
      `Force-logout ${account.name || account.phone || account.email}?\n\nThis ends all their active sessions (they must sign in again). It does NOT block the account.`
    );
    if (!confirmed) return;
    setActionLoading(true);
    try {
      const res = await adminAPI.revokeUserSessions({ userId: account._id });
      setMessage({ type: 'success', text: res.data?.message || 'Sessions revoked. The user must sign in again.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.message || 'Failed to revoke sessions' });
    } finally {
      setActionLoading(false);
    }
  };

  const updateBlockStatus = async (accountId, isBlocked, reason) => {
    setActionLoading(true);
    setMessage({ type: '', text: '' });

    try {
      let response;

      switch (accountType) {
        case 'users':
          response = await adminAPI.updateUserBlockStatus(accountId, isBlocked, reason);
          break;
        case 'restaurants':
          response = await adminAPI.updateRestaurantBlockStatus(accountId, isBlocked, reason);
          break;
        case 'delivery':
          response = await adminAPI.updateDeliveryPartnerBlockStatus(accountId, isBlocked, reason);
          break;
        default:
          return;
      }

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: `Account ${isBlocked ? 'blocked' : 'unblocked'} successfully!`
        });

        // Update local state
        setAccounts(prevAccounts =>
          prevAccounts.map(account =>
            account._id === accountId
              ? {
                  ...account,
                  isBlocked,
                  blockedReason: isBlocked ? reason : null,
                  blockedAt: isBlocked ? new Date() : null
                }
              : account
          )
        );
      }
    } catch (error) {
      console.error('Failed to update block status:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update block status. Please try again.'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewDetails = (account) => {
    console.log('[AccountManagement] Opening details for account:', account._id);
    console.log('[AccountManagement] Account has subscriptionPlan:', !!account.subscriptionPlan);
    if (account.subscriptionPlan) {
      console.log('[AccountManagement] subscriptionPlan data:', JSON.stringify(account.subscriptionPlan, null, 2));
    }
    setAccountDetails(account);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setAccountDetails(null);
  };

  // Open location update modal for delivery partner
  const handleOpenLocationModal = (account) => {
    setSelectedAccount(account);
    // Pre-fill with existing location if available
    const currentLocation = account.deliveryPartner?.currentLocation || account.location || {};
    setLocationData({
      lat: currentLocation.lat || '',
      lng: currentLocation.lng || ''
    });
    setShowLocationModal(true);
  };

  // Close location modal
  const closeLocationModal = () => {
    setShowLocationModal(false);
    setSelectedAccount(null);
    setLocationData({ lat: '', lng: '' });
  };

  // Handle location update submission
  const handleUpdateLocation = async () => {
    if (!locationData.lat || !locationData.lng) {
      alert('Please provide both latitude and longitude.');
      return;
    }

    const lat = parseFloat(locationData.lat);
    const lng = parseFloat(locationData.lng);

    if (isNaN(lat) || isNaN(lng)) {
      alert('Please enter valid numeric values for coordinates.');
      return;
    }

    if (lat < -90 || lat > 90) {
      alert('Latitude must be between -90 and 90.');
      return;
    }

    if (lng < -180 || lng > 180) {
      alert('Longitude must be between -180 and 180.');
      return;
    }

    setLocationLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await adminAPI.updateDeliveryPartnerLocation(selectedAccount._id, { lat, lng });

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: `Location updated successfully for ${selectedAccount.name || selectedAccount.email}!`
        });

        // Update local state
        setAccounts(prevAccounts =>
          prevAccounts.map(account =>
            account._id === selectedAccount._id
              ? {
                  ...account,
                  deliveryPartner: {
                    ...account.deliveryPartner,
                    currentLocation: { lat, lng, lastUpdated: new Date() }
                  },
                  location: { lat, lng }
                }
              : account
          )
        );

        // Update accountDetails if it's the same account
        if (accountDetails && accountDetails._id === selectedAccount._id) {
          setAccountDetails({
            ...accountDetails,
            deliveryPartner: {
              ...accountDetails.deliveryPartner,
              currentLocation: { lat, lng, lastUpdated: new Date() }
            },
            location: { lat, lng }
          });
        }

        closeLocationModal();
      }
    } catch (error) {
      console.error('Failed to update location:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update location. Please try again.'
      });
    } finally {
      setLocationLoading(false);
    }
  };

  // Client-side search over the loaded list (name / phone / email).
  const _q = search.trim().toLowerCase();
  const visibleAccounts = _q
    ? accounts.filter(a =>
        (a.name || '').toLowerCase().includes(_q) ||
        (a.phone || '').includes(search.trim()) ||
        (a.email || '').toLowerCase().includes(_q))
    : accounts;

  return (
    <div className="account-management">
      <div className="account-management-header">
        <h2>🛡️ Account Management</h2>
        <p>Block or unblock user, restaurant, and delivery partner accounts</p>
      </div>

      {/* Account Type Selector */}
      <div className="account-type-selector">
        <button
          className={`account-type-btn ${accountType === 'users' ? 'active' : ''}`}
          onClick={() => setAccountType('users')}
        >
          👥 Users ({accounts.filter(() => accountType === 'users').length})
        </button>
        <button
          className={`account-type-btn ${accountType === 'restaurants' ? 'active' : ''}`}
          onClick={() => setAccountType('restaurants')}
        >
          🏪 Restaurants ({accounts.filter(() => accountType === 'restaurants').length})
        </button>
        <button
          className={`account-type-btn ${accountType === 'delivery' ? 'active' : ''}`}
          onClick={() => setAccountType('delivery')}
        >
          🚴 Delivery Partners ({accounts.filter(() => accountType === 'delivery').length})
        </button>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`account-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Account List */}
      <div className="account-list-section">
        <div className="list-header">
          <h3>
            {accountType === 'users' && '👥 Users List'}
            {accountType === 'restaurants' && '🏪 Restaurants List'}
            {accountType === 'delivery' && '🚴 Delivery Partners List'}
            <span style={{ fontSize: 13, color: '#888', fontWeight: 400, marginLeft: 8 }}>
              ({visibleAccounts.length}{_q ? ` of ${accounts.length}` : ''})
            </span>
          </h3>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Search name / phone / email…"
            style={{ flex: 1, maxWidth: 320, margin: '0 12px', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8 }}
          />
          <button onClick={fetchAccounts} className="refresh-btn" disabled={loading}>
            🔄 Refresh
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading {accountType}...</p>
          </div>
        ) : visibleAccounts.length === 0 ? (
          <div className="empty-state">
            <p>{_q ? `No ${accountType} match "${search.trim()}".` : `No ${accountType} found.`}</p>
          </div>
        ) : (
          <div className="account-table-container">
            <table className="account-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Blocked At</th>
                  <th>Block Reason</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleAccounts.map(account => (
                  <tr
                    key={account._id}
                    className={`${account.isBlocked ? 'blocked-account' : ''} clickable-row`}
                    onClick={() => handleViewDetails(account)}
                  >
                    <td>{account.name || 'N/A'}</td>
                    <td>{account.email || account.phone || 'N/A'}</td>
                    <td>
                      <span className={`status-badge ${account.isBlocked ? 'blocked' : 'active'}`}>
                        {account.isBlocked ? '🔴 Blocked' : '🟢 Active'}
                      </span>
                    </td>
                    <td>{formatDate(account.blockedAt)}</td>
                    <td className="block-reason">
                      {account.isBlocked ? (account.blockedReason || 'No reason provided') : '-'}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {account.isBlocked ? (
                        <button
                          className="action-btn unblock-btn"
                          onClick={() => handleUnblockAction(account)}
                          disabled={actionLoading}
                        >
                          ✅ Unblock
                        </button>
                      ) : (
                        <button
                          className="action-btn block-btn"
                          onClick={() => handleBlockAction(account)}
                          disabled={actionLoading}
                        >
                          🚫 Block
                        </button>
                      )}
                      {accountType !== 'restaurants' && (
                        <button
                          className="action-btn"
                          onClick={() => handleRevokeSessions(account)}
                          disabled={actionLoading}
                          title="End all active sessions (force sign-out) without blocking"
                          style={{ marginLeft: 6 }}
                        >
                          🔒 Revoke Sessions
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Block Reason Modal */}
      {showBlockModal && (
        <div className="modal-overlay">
          <div className="block-modal">
            <div className="modal-header">
              <h3>🚫 Block Account</h3>
              <button
                className="close-modal-btn"
                onClick={() => {
                  setShowBlockModal(false);
                  setSelectedAccount(null);
                  setBlockReason('');
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                You are about to block:{' '}
                <strong>{selectedAccount?.name || selectedAccount?.email}</strong>
              </p>
              <div className="form-group">
                <label htmlFor="blockReason">Reason for Blocking *</label>
                <textarea
                  id="blockReason"
                  className="block-reason-input"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="Enter the reason for blocking this account..."
                  rows="4"
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="modal-btn cancel-btn"
                onClick={() => {
                  setShowBlockModal(false);
                  setSelectedAccount(null);
                  setBlockReason('');
                }}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="modal-btn submit-btn"
                onClick={handleBlockSubmit}
                disabled={actionLoading || !blockReason.trim()}
              >
                {actionLoading ? 'Blocking...' : '🚫 Block Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Details Modal */}
      {showDetailsModal && accountDetails && (
        <div className="modal-overlay" onClick={closeDetailsModal}>
          <div className="details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {accountType === 'users' && '👤 User Details'}
                {accountType === 'restaurants' && '🏪 Restaurant Details'}
                {accountType === 'delivery' && '🚴 Delivery Partner Details'}
              </h3>
              <button className="close-modal-btn" onClick={closeDetailsModal}>×</button>
            </div>

            <div className="modal-body details-content">
              {/* User Details */}
              {accountType === 'users' && (
                <div className="details-grid">
                  <div className="detail-section">
                    <h4>📋 Basic Information</h4>
                    <div className="detail-item">
                      <span className="detail-label">Name:</span>
                      <span className="detail-value">{accountDetails.name || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{accountDetails.email || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{accountDetails.phone || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">User ID:</span>
                      <span className="detail-value mono">{accountDetails._id}</span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>📍 Address Information</h4>
                    {accountDetails.addresses && accountDetails.addresses.length > 0 ? (
                      accountDetails.addresses.map((addr, idx) => (
                        <div key={idx} className="address-block">
                          <p><strong>{addr.type || 'Address'} {addr.isDefault && '(Default)'}</strong></p>
                          <p>{addr.street}</p>
                          <p>{addr.city}, {addr.state} {addr.postalCode}</p>
                          <p>{addr.country}</p>
                        </div>
                      ))
                    ) : (
                      <p className="no-data">No addresses added</p>
                    )}
                  </div>

                  <div className="detail-section">
                    <h4>🛡️ Account Status</h4>
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span className={`status-badge ${accountDetails.isBlocked ? 'blocked' : 'active'}`}>
                        {accountDetails.isBlocked ? '🔴 Blocked' : '🟢 Active'}
                      </span>
                    </div>
                    {accountDetails.isBlocked && (
                      <>
                        <div className="detail-item">
                          <span className="detail-label">Blocked At:</span>
                          <span className="detail-value">{formatDate(accountDetails.blockedAt)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Block Reason:</span>
                          <span className="detail-value">{accountDetails.blockedReason || 'No reason provided'}</span>
                        </div>
                      </>
                    )}
                    <div className="detail-item">
                      <span className="detail-label">Joined:</span>
                      <span className="detail-value">{formatDate(accountDetails.createdAt)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Restaurant Details */}
              {accountType === 'restaurants' && (
                <div className="details-grid">
                  <div className="detail-section">
                    <h4>📋 Basic Information</h4>
                    <div className="detail-item">
                      <span className="detail-label">Restaurant Name:</span>
                      <span className="detail-value">{accountDetails.name || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{accountDetails.email || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{accountDetails.phone || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Restaurant ID:</span>
                      <span className="detail-value mono">{accountDetails._id}</span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>🍽️ Restaurant Details</h4>
                    <div className="detail-item">
                      <span className="detail-label">Cuisine:</span>
                      <span className="detail-value">{accountDetails.cuisine || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Rating:</span>
                      <span className="detail-value">⭐ {accountDetails.rating || 0} ({accountDetails.totalRatings || 0} reviews)</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span className="detail-value">{accountDetails.isOpen ? '🟢 Open' : '🔴 Closed'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Delivery Time:</span>
                      <span className="detail-value">{accountDetails.deliveryTime || 'N/A'} mins</span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>📍 Location</h4>
                    {accountDetails.address ? (
                      <>
                        <p>{accountDetails.address.street}</p>
                        <p>{accountDetails.address.city}, {accountDetails.address.state} {accountDetails.address.postalCode}</p>
                        <p>{accountDetails.address.country}</p>
                      </>
                    ) : (
                      <p className="no-data">No address added</p>
                    )}
                  </div>

                  <div className="detail-section">
                    <h4>💳 Subscription</h4>
                    <div className="detail-item">
                      <span className="detail-label">Plan Type:</span>
                      <span className="detail-value">
                        {accountDetails.subscriptionPlan?.type === 'one_time' ? 'One-Time Fee' :
                         accountDetails.subscriptionPlan?.type === 'commission' ? 'Commission Based' :
                         accountDetails.subscriptionPlan?.type || 'N/A'}
                      </span>
                    </div>
                    {accountDetails.subscriptionPlan?.type === 'one_time' && (
                      <>
                        <div className="detail-item">
                          <span className="detail-label">Initial Payment:</span>
                          <span className="detail-value">
                            ₹{accountDetails.subscriptionPlan.oneTimeFee?.initialPayment?.toLocaleString() || 'N/A'}
                            ({accountDetails.subscriptionPlan.oneTimeFee?.initialPaymentStatus || 'N/A'})
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Remaining Payment:</span>
                          <span className="detail-value">
                            ₹{accountDetails.subscriptionPlan.oneTimeFee?.remainingPayment?.toLocaleString() || 'N/A'}
                            ({accountDetails.subscriptionPlan.oneTimeFee?.remainingPaymentStatus || 'N/A'})
                          </span>
                        </div>
                        {accountDetails.subscriptionPlan.oneTimeFee?.trialStartDate && (
                          <div className="detail-item">
                            <span className="detail-label">Trial Period:</span>
                            <span className="detail-value">
                              {formatDate(accountDetails.subscriptionPlan.oneTimeFee.trialStartDate)} - {formatDate(accountDetails.subscriptionPlan.oneTimeFee.trialEndDate)}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {accountDetails.subscriptionPlan?.type === 'commission' && (
                      <>
                        <div className="detail-item">
                          <span className="detail-label">Commission Rate:</span>
                          <span className="detail-value">{accountDetails.subscriptionPlan.commission?.percentage || 0}%</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Total Orders:</span>
                          <span className="detail-value">{accountDetails.subscriptionPlan.commission?.totalOrdersProcessed || 0}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Commission Earned:</span>
                          <span className="detail-value">₹{accountDetails.subscriptionPlan.commission?.totalCommissionEarned?.toLocaleString() || 0}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="detail-section">
                    <h4>🛡️ Account Status</h4>
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span className={`status-badge ${accountDetails.isBlocked ? 'blocked' : 'active'}`}>
                        {accountDetails.isBlocked ? '🔴 Blocked' : '🟢 Active'}
                      </span>
                    </div>
                    {accountDetails.isBlocked && (
                      <>
                        <div className="detail-item">
                          <span className="detail-label">Blocked At:</span>
                          <span className="detail-value">{formatDate(accountDetails.blockedAt)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Block Reason:</span>
                          <span className="detail-value">{accountDetails.blockedReason || 'No reason provided'}</span>
                        </div>
                      </>
                    )}
                    <div className="detail-item">
                      <span className="detail-label">Registered:</span>
                      <span className="detail-value">{formatDate(accountDetails.createdAt)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Delivery Partner Details */}
              {accountType === 'delivery' && (
                <div className="details-grid">
                  <div className="detail-section">
                    <h4>📋 Basic Information</h4>
                    <div className="detail-item">
                      <span className="detail-label">Name:</span>
                      <span className="detail-value">{accountDetails.name || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{accountDetails.email || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{accountDetails.phone || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Partner ID:</span>
                      <span className="detail-value mono">{accountDetails._id}</span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>🚗 Vehicle Information</h4>
                    <div className="detail-item">
                      <span className="detail-label">Vehicle Type:</span>
                      <span className="detail-value">{accountDetails.deliveryPartner?.vehicleType || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Vehicle Number:</span>
                      <span className="detail-value">{accountDetails.deliveryPartner?.vehicleNumber || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">License Number:</span>
                      <span className="detail-value">{accountDetails.deliveryPartner?.licenseNumber || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>📊 Performance</h4>
                    <div className="detail-item">
                      <span className="detail-label">Rating:</span>
                      <span className="detail-value">⭐ {accountDetails.deliveryPartner?.rating || 0}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Total Deliveries:</span>
                      <span className="detail-value">{accountDetails.deliveryPartner?.totalDeliveries || 0}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span className="detail-value">
                        {accountDetails.deliveryPartner?.isAvailable ? '🟢 Available' : '🔴 Unavailable'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Approval:</span>
                      <span className="detail-value">
                        {accountDetails.deliveryPartner?.isApproved ? '✅ Approved' : '⏳ Pending'}
                      </span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>🛡️ Account Status</h4>
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span className={`status-badge ${accountDetails.isBlocked ? 'blocked' : 'active'}`}>
                        {accountDetails.isBlocked ? '🔴 Blocked' : '🟢 Active'}
                      </span>
                    </div>
                    {accountDetails.isBlocked && (
                      <>
                        <div className="detail-item">
                          <span className="detail-label">Blocked At:</span>
                          <span className="detail-value">{formatDate(accountDetails.blockedAt)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Block Reason:</span>
                          <span className="detail-value">{accountDetails.blockedReason || 'No reason provided'}</span>
                        </div>
                      </>
                    )}
                    <div className="detail-item">
                      <span className="detail-label">Joined:</span>
                      <span className="detail-value">{formatDate(accountDetails.createdAt)}</span>
                    </div>
                  </div>

                  {/* Location Section for Delivery Partners */}
                  <div className="detail-section">
                    <h4>📍 Current Location</h4>
                    <div className="detail-item">
                      <span className="detail-label">Latitude:</span>
                      <span className="detail-value">
                        {accountDetails.deliveryPartner?.currentLocation?.lat || accountDetails.location?.lat || 'Not set'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Longitude:</span>
                      <span className="detail-value">
                        {accountDetails.deliveryPartner?.currentLocation?.lng || accountDetails.location?.lng || 'Not set'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Last Updated:</span>
                      <span className="detail-value">
                        {formatDate(accountDetails.deliveryPartner?.currentLocation?.lastUpdated) || 'Never'}
                      </span>
                    </div>
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <button
                        className="action-btn location-btn"
                        onClick={() => handleOpenLocationModal(accountDetails)}
                        style={{
                          background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          color: 'white',
                          padding: '10px 20px',
                          fontSize: '14px'
                        }}
                      >
                        📍 Update Location
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Location Update Modal */}
      {showLocationModal && (
        <div className="modal-overlay">
          <div className="block-modal">
            <div className="modal-header">
              <h3>📍 Update Delivery Partner Location</h3>
              <button className="close-modal-btn" onClick={closeLocationModal}>×</button>
            </div>
            <div className="modal-body">
              <p>
                Updating location for: <strong>{selectedAccount?.name || selectedAccount?.email}</strong>
              </p>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label htmlFor="latitude">Latitude *</label>
                <input
                  type="number"
                  id="latitude"
                  className="block-reason-input"
                  value={locationData.lat}
                  onChange={(e) => setLocationData({ ...locationData, lat: e.target.value })}
                  placeholder="e.g., 28.6139"
                  step="any"
                  min="-90"
                  max="90"
                  style={{ height: 'auto', padding: '12px' }}
                />
                <small style={{ color: '#6c757d', fontSize: '12px' }}>
                  Valid range: -90 to 90
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="longitude">Longitude *</label>
                <input
                  type="number"
                  id="longitude"
                  className="block-reason-input"
                  value={locationData.lng}
                  onChange={(e) => setLocationData({ ...locationData, lng: e.target.value })}
                  placeholder="e.g., 77.2090"
                  step="any"
                  min="-180"
                  max="180"
                  style={{ height: 'auto', padding: '12px' }}
                />
                <small style={{ color: '#6c757d', fontSize: '12px' }}>
                  Valid range: -180 to 180
                </small>
              </div>
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: '#e3f2fd',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#1565c0'
              }}>
                💡 <strong>Tip:</strong> You can get coordinates from Google Maps by right-clicking on any location and selecting "What's here?"
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="modal-btn cancel-btn"
                onClick={closeLocationModal}
                disabled={locationLoading}
              >
                Cancel
              </button>
              <button
                className="modal-btn"
                onClick={handleUpdateLocation}
                disabled={locationLoading || !locationData.lat || !locationData.lng}
                style={{
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: 'white'
                }}
              >
                {locationLoading ? 'Updating...' : '📍 Update Location'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="instructions-panel">
        <h4>📖 Instructions</h4>
        <ul>
          <li>Select an account type (Users, Restaurants, or Delivery Partners)</li>
          <li><strong>Click on any row</strong> to view complete account details</li>
          <li>Click "🚫 Block" to block an account (you must provide a reason)</li>
          <li>Click "✅ Unblock" to restore access to a blocked account</li>
          <li>Blocked accounts cannot log in or access the system</li>
          <li>All blocking actions are tracked with timestamp and reason</li>
          <li><strong>For Delivery Partners:</strong> Click "📍 Update Location" in details to manually set their GPS coordinates</li>
        </ul>
      </div>
    </div>
  );
};

export default AccountManagement;
